import React, { useState, useEffect } from "react";
import { Button } from "../../../shadcn/ui/button";
import { Card, CardContent } from "../../../shadcn/ui/card";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../shadcn/ui/popover";
import { Calendar as CalendarComponent } from "../../../shadcn/ui/calendar";

import { MapPin, Settings, Building2, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { useHistory } from "react-router-dom";
import { showApiMessage } from "../../../utils/messageHandler";
import { getEventName } from "../../../utils/spaceNamingUtils";
import { amenitiesService } from "../../../services/amenitiesService";
import { buildingAddressService } from "../../../services/buildingAddressService";

// Import components
import BookingViewToggle from "./components/BookingViewToggle";
import SpaceGridView from "./components/SpaceGridView";
import SpaceListView from "./components/SpaceListView";
import SpaceCalendarView from "./components/SpaceCalendarView";
import SpaceMapView from "./components/SpaceMapView";

// Import the real space management service
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { formatDurationType } from "../../../utils/bookingTimeframeUtils";
import logger from "../../../utils/logger";


const HostCustomerBookSpace = () => {
  const history = useHistory();
  // State management
  const [initialLoading, setInitialLoading] = useState(true);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [filters, setFilters] = useState({
    location: null,
    startDate: null,
    endDate: null,
    startTime: "",
    endTime: "",
    amenities: [],
  });
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [buildingAddresses, setBuildingAddresses] = useState([]);
  const [buildingAddressesPage, setBuildingAddressesPage] = useState(0);
  const [buildingAddressesHasMore, setBuildingAddressesHasMore] = useState(true);
  const [buildingAddressesLoading, setBuildingAddressesLoading] = useState(false);
  const [showAmenitiesPopover, setShowAmenitiesPopover] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch amenities on mount
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await amenitiesService.getAllAmenities();
        if (response.status === 200) {
          const amenitiesData = response.data?.content || response.data?.data || response.data || [];
          setAmenitiesList(Array.isArray(amenitiesData) ? amenitiesData : []);
        }
      } catch (error) {
        logger.error("Error fetching amenities:", error);
        setAmenitiesList([]);
      }
    };

    fetchAmenities();
  }, []);

  // Fetch building addresses with pagination
  const fetchBuildingAddresses = async (page = 0, append = false) => {
    if (buildingAddressesLoading) return;

    try {
      setBuildingAddressesLoading(true);
      const response = await buildingAddressService.getAccessibleBuildingAddresses({
        pageNumber: page,
        pageSize: 20,
        sort: { sorted: true },
        hasSpaces: true,
      });

      if (response.status === 200) {
        const data = response.data?.content || response.data?.data || [];
        const totalPages = response.data?.totalPages || 1;

        if (append) {
          setBuildingAddresses(prev => [...prev, ...data]);
        } else {
          setBuildingAddresses(data);
        }

        setBuildingAddressesHasMore(page + 1 < totalPages);
        setBuildingAddressesPage(page);
      }
    } catch (error) {
      logger.error("Error fetching building addresses:", error);
    } finally {
      setBuildingAddressesLoading(false);
    }
  };

  // Load more building addresses
  const handleLoadMoreBuildingAddresses = () => {
    if (buildingAddressesHasMore && !buildingAddressesLoading) {
      fetchBuildingAddresses(buildingAddressesPage + 1, true);
    }
  };

  // Fetch initial building addresses on mount
  useEffect(() => {
    fetchBuildingAddresses(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transformSpacesData = spaces => {
    return (spaces || []).map(space => {
      const firstInstance = space.instances?.[0];
      const availableSlots =
        space.availableSlots?.length > 0 ? space.availableSlots : firstInstance?.availabilitySlots || [];

      return {
        ...space,
        name: firstInstance?.title || getEventName(space, firstInstance) || space.name || `Space #${space.id}`,
        image: space.media?.images?.[0] || space.images?.[0] || "/img/placeholder-space.jpg",
        type: space.spaceType || "HIGH",
        location: (() => {
          const buildingAddress = firstInstance?.location?.buildingAddress;
          if (buildingAddress) {
            const parts = [
              buildingAddress.buildingName,
              buildingAddress.city,
              buildingAddress.region,
              buildingAddress.country,
            ].filter(Boolean);
            return parts.length > 0 ? parts.join(", ") : "No address";
          }
          return firstInstance?.location?.name || "No address";
        })(),
        pricing: space.ratePrice
          ? `${space.currency || ""} ${space.ratePrice} ${formatDurationType(space.duration || "HOURLY")}`
          : "Contact for pricing",
        bookingData: space.bookingData || [],
        availableSlots: availableSlots,
        minimumDuration: space.minimumDuration || 1,
        maximumDuration: space.maximumDuration || 24,
        capacity: space.capacity || 1,
        coordinates: (() => {
          const buildingAddress = firstInstance?.location?.buildingAddress;
          if (buildingAddress?.latitude && buildingAddress?.longitude) {
            return {
              latitude: buildingAddress.latitude,
              longitude: buildingAddress.longitude,
            };
          }
          return null;
        })(),
        instanceCount: space.availableInstancesCount || space.instances?.length || 0,
      };
    });
  };

  // Fetch data when component loads or filters change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setSpacesLoading(true);
        setError(null);

        // Build filter object for authenticated search API
        const apiFilters = {
          status: "PUBLISHED",
          page: 0,
          size: 100,
          "sort.sorted": true,
        };

        // Add buildingAddressId if location is selected
        if (filters.location) {
          apiFilters.buildingAddressId = filters.location;
        }
        if (filters.startDate) apiFilters.startDate = filters.startDate;
        if (filters.endDate) apiFilters.endDate = filters.endDate;
        if (filters.startTime) apiFilters.startTime = filters.startTime;
        if (filters.endTime) apiFilters.endTime = filters.endTime;

        // Add amenity IDs as comma-separated string
        if (Array.isArray(filters.amenities) && filters.amenities.length > 0) {
          apiFilters.amenityIds = filters.amenities.map(id => String(id)).join(",");
        }

        // Add search query if present
        if (searchQuery) {
          apiFilters.spaceName = searchQuery;
          apiFilters.addressContains = searchQuery;
        }

        // Fetch spaces from authenticated search API
        const response = await spaceManagementService.searchSpaces(apiFilters);

        if (response.status === 200) {
          const transformedSpaces = transformSpacesData(response.data.content || response.data.data || []);

          setPageData({
            spaces: transformedSpaces,
            filters: {},
            statistics: null,
            days: [],
            timeSlots: [],
          });
        } else {
          throw new Error("Failed to fetch space data");
        }

        setSpacesLoading(false);
        setInitialLoading(false);
      } catch (err) {
        logger.error("Error fetching book space data:", err);
        setError(err.message || "An error occurred while fetching spaces");
        setSpacesLoading(false);
        setInitialLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  // Fetch statistics on component mount
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        // Fetch both space and booking statistics
        const [spacesStats, bookingsStats] = await Promise.all([
          spaceManagementService.getSpaceStatistics(),
          spaceManagementService.getBookingStatistics(),
        ]);

        if (spacesStats.status === 200 && bookingsStats.status === 200) {
          const spaceData = spacesStats.data;
          const bookingData = bookingsStats.data;

          setStatistics({
            totalSpaces: spaceData.totalSpaces || 0,
            availableSpaces: spaceData.availableSpaces || 0,
            bookedSpaces: spaceData.bookedSpaces || 0,
            occupiedSpaces: spaceData.occupiedSpaces || 0,
            occupancyRate: spaceData.occupancyRate || 0,
            bookingRate: spaceData.bookingRate || 0,
            totalBookings: bookingData.totalBookings || 0,
            totalActiveBookings: bookingData.totalActiveBookings || 0,
            percentageOccupancyRate: bookingData.percentageOccupancyRate || 0,
          });
        }
      } catch (error) {
        logger.error("Error fetching statistics:", error);
        // Set default values on error
        setStatistics({
          totalSpaces: 0,
          availableSpaces: 0,
          bookedSpaces: 0,
          occupiedSpaces: 0,
          occupancyRate: 0,
          bookingRate: 0,
          totalBookings: 0,
          totalActiveBookings: 0,
          percentageOccupancyRate: 0,
        });
      }
    };

    fetchStatistics();
  }, []); // Empty dependency array - fetch once on mount

  // Filter spaces based on search query
  const filteredSpaces =
    pageData?.spaces?.filter(space => {
      if (!searchQuery) return true;
      return (
        space.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        space.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        space.type.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }) || [];

  // Handler functions
  const handleSelectSpace = space => {
    setSelectedSpace(space);
    setViewMode("calendar"); // Switch to calendar view when a space is selected
    if (space && space.id) {
      history.push(`/host/space/${space.id}`);
    }
  };

  const handleBookSlot = (time, dayIndex, spaceId) => {
    const space = pageData?.spaces?.find(s => s.id === spaceId);
    const day = pageData?.days?.[dayIndex];

    if (space && day) {
      logger.log(`Booking ${time} on ${day.name} for ${space.name}`);
      // Here you would typically make an API call to book the slot
      showApiMessage(
        { status: 200, message: `Booking request for ${space.name} on ${day.name} at ${time} submitted` },
        "book slot"
      );
    }
  };

  const handleBackToSpaces = () => {
    setSelectedSpace(null);
    setViewMode("grid"); // Return to grid view
  };

  // eslint-disable-next-line no-unused-vars
  const _handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  // Initial loading state
  if (initialLoading) {
    return (
      <HPageContainer>
        <HHeader />
        <div className="overflow-y-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="mx-auto space-y-6 max-w-7xl animate-pulse">
            {/* Header skeleton */}
            <div className="space-y-2">
              <div className="w-1/3 h-8 bg-gray-200 rounded"></div>
              <div className="w-1/2 h-4 bg-gray-200 rounded"></div>
            </div>

            {/* Statistics skeleton */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-6 h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>

            {/* Filters skeleton */}
            <div className="p-6 h-32 bg-gray-200 rounded-lg"></div>

            {/* Content skeleton */}
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <HPageContainer>
        <HHeader />
        <div className="overflow-y-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="flex flex-col justify-center items-center h-96 text-center">
            <div className="p-6 max-w-md bg-red-50 rounded-lg border border-red-200">
              <h3 className="mb-2 text-lg font-medium text-red-800">Error Loading Spaces</h3>
              <p className="mb-4 text-red-600">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-white bg-red-600 rounded-md transition-colors hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  // Render view content based on mode and selected space
  const renderView = () => {
    if (spacesLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-80 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      );
    }

    if (!filteredSpaces) return null;

    if (filteredSpaces.length === 0) {
      return (
        <div className="flex flex-col justify-center items-center h-64 text-center">
          <Building2 className="mb-4 w-16 h-16 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            {searchQuery || Object.keys(filters).length > 0 ? "No Spaces Found" : "No Spaces Available"}
          </h3>
          <p className="mb-4 text-gray-600">
            {searchQuery || Object.keys(filters).length > 0
              ? "Try adjusting your search or filter criteria."
              : "There are currently no spaces available for booking."}
          </p>
          {(searchQuery || Object.keys(filters).length > 0) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setFilters({
                  location: null,
                  startDate: null,
                  endDate: null,
                  startTime: "",
                  endTime: "",
                  amenities: [],
                });
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      );
    }

    switch (viewMode) {
      case "grid":
        return <SpaceGridView spaces={filteredSpaces} onSelectSpace={handleSelectSpace} />;
      case "list":
        return <SpaceListView spaces={filteredSpaces} onSelectSpace={handleSelectSpace} />;
      case "calendar":
        if (selectedSpace) {
          return (
            <SpaceCalendarView
              space={selectedSpace}
              bookingData={selectedSpace.bookingData}
              timeSlots={pageData?.timeSlots || []}
              days={pageData?.days || []}
              onBookSlot={handleBookSlot}
            />
          );
        }
        return <SpaceGridView spaces={filteredSpaces} onSelectSpace={handleSelectSpace} />;
      case "map":
        return <SpaceMapView spaces={filteredSpaces} onSelectSpace={handleSelectSpace} />;
      default:
        return <SpaceGridView spaces={filteredSpaces} onSelectSpace={handleSelectSpace} />;
    }
  };

  return (
    <HPageContainer>
      <HHeader />

      <div className="overflow-y-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="mx-auto space-y-6 max-w-7xl">
          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="p-6">
                  <div className="text-sm font-medium text-gray-600 mb-2">Total Listings</div>
                  <div className="text-3xl font-bold">{statistics.totalSpaces}</div>
                  <p className="mt-1 text-xs text-gray-500">{statistics.totalBookings} total bookings</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="text-sm font-medium text-gray-600 mb-2">Available Now</div>
                  <div className="text-3xl font-bold text-green-600">{statistics.availableSpaces}</div>
                  <p className="mt-1 text-sm text-green-600">
                    {statistics.totalSpaces > 0
                      ? Math.round((statistics.availableSpaces / statistics.totalSpaces) * 100)
                      : 0}
                    % available
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="text-sm font-medium text-gray-600 mb-2">Currently Booked</div>
                  <div className="text-3xl font-bold text-blue-600">{statistics.bookedSpaces}</div>
                  <p className="mt-1 text-sm text-blue-600">
                    {statistics.totalActiveBookings} active{" "}
                    {statistics.totalActiveBookings === 1 ? "booking" : "bookings"}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="text-sm font-medium text-gray-600 mb-2">Booking Rate</div>
                  <div className="text-3xl font-bold">{statistics.bookingRate.toFixed(1)}%</div>
                  <div className="mt-2 w-full h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-blue-600 rounded-full transition-all"
                      style={{ width: `${Math.min(statistics.bookingRate, 100)}%` }}
                    ></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          {pageData?.filters && (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Location and Amenities Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Location */}
                    <div>
                      <Label>Location</Label>
                      <Select
                        value={filters.location?.toString() || "all"}
                        onValueChange={value =>
                          setFilters(prev => ({ ...prev, location: value === "all" ? null : parseInt(value) }))
                        }
                      >
                        <SelectTrigger className="mt-1 w-full">
                          <MapPin className="mr-2 w-4 h-4" />
                          <SelectValue placeholder="All Locations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Locations</SelectItem>
                          {buildingAddresses.map(address => (
                            <SelectItem key={address.id} value={address.id.toString()}>
                              {address.buildingName || `${address.city}, ${address.region}`}
                            </SelectItem>
                          ))}
                          {buildingAddressesHasMore && (
                            <div className="p-2 border-t">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full"
                                onClick={handleLoadMoreBuildingAddresses}
                                disabled={buildingAddressesLoading}
                              >
                                {buildingAddressesLoading ? "Loading..." : "Load More"}
                              </Button>
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Amenities */}
                    <div>
                      <Label>Amenities</Label>
                      <Popover open={showAmenitiesPopover} onOpenChange={setShowAmenitiesPopover}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full mt-1 justify-start">
                            <Settings className="mr-2 w-4 h-4" />
                            {filters.amenities.length > 0 ? `${filters.amenities.length} selected` : "Select Amenities"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80" align="start">
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm mb-3">Select Amenities</h4>
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {Array.isArray(amenitiesList) && amenitiesList.length > 0 ? (
                                amenitiesList.map(amenity => (
                                  <div key={amenity.id} className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      id={`amenity-${amenity.id}`}
                                      checked={filters.amenities.includes(amenity.id)}
                                      onChange={e => {
                                        setFilters(prev => ({
                                          ...prev,
                                          amenities: e.target.checked
                                            ? [...prev.amenities, amenity.id]
                                            : prev.amenities.filter(id => id !== amenity.id),
                                        }));
                                      }}
                                      className="rounded border-gray-300"
                                    />
                                    <label htmlFor={`amenity-${amenity.id}`} className="text-sm cursor-pointer">
                                      {amenity.label || amenity.name}
                                    </label>
                                  </div>
                                ))
                              ) : (
                                <div className="text-sm text-gray-500 text-center py-4">No amenities available</div>
                              )}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Date & Time Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Start Date & Time */}
                    <div>
                      <Label>Start Date & Time</Label>
                      <div className="flex gap-2 mt-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.startDate ? format(filters.startDate, "MMM dd") : "Select a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={filters.startDate}
                              onSelect={date => setFilters(prev => ({ ...prev, startDate: date }))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <Select
                          value={filters.startTime}
                          onValueChange={value => setFilters(prev => ({ ...prev, startTime: value }))}
                        >
                          <SelectTrigger className="w-full min-w-[100px]">
                            <SelectValue placeholder="Time" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour = i.toString().padStart(2, "0");
                              return (
                                <SelectItem key={hour} value={`${hour}:00`}>
                                  {hour}:00
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* End Date & Time */}
                    <div>
                      <Label>End Date & Time</Label>
                      <div className="flex gap-2 mt-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.endDate ? format(filters.endDate, "MMM dd") : "Select a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={filters.endDate}
                              onSelect={date => setFilters(prev => ({ ...prev, endDate: date }))}
                              initialFocus
                              disabled={date => filters.startDate && date < filters.startDate}
                            />
                          </PopoverContent>
                        </Popover>
                        <Select
                          value={filters.endTime}
                          onValueChange={value => setFilters(prev => ({ ...prev, endTime: value }))}
                        >
                          <SelectTrigger className="w-full min-w-[100px]">
                            <SelectValue placeholder="Time" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour = i.toString().padStart(2, "0");
                              return (
                                <SelectItem key={hour} value={`${hour}:00`}>
                                  {hour}:00
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and View Controls */}
          <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search spaces..."
                className="w-full"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <BookingViewToggle activeView={viewMode} onViewChange={setViewMode} />
          </div>

          {/* Content based on view mode and selected space */}
          {viewMode === "calendar" && selectedSpace ? (
            <div className="space-y-4">
              <div className="flex gap-4 items-center">
                <Button variant="outline" onClick={handleBackToSpaces}>
                  ← Back to Spaces
                </Button>
                <h2 className="text-xl font-semibold">Book {selectedSpace.name}</h2>
              </div>
              {renderView()}
            </div>
          ) : (
            <>
              {/* Legend */}
              <div className="flex justify-center">
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-100 rounded border border-green-300"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-orange-100 rounded border border-orange-300"></div>
                    <span>Booked</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-red-100 rounded border border-red-300"></div>
                    <span>Occupied</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-100 rounded border border-gray-300"></div>
                    <span>Maintenance</span>
                  </div>
                </div>
              </div>

              {/* Main content view */}
              {renderView()}
            </>
          )}
        </div>
      </div>
    </HPageContainer>
  );
};

export default HostCustomerBookSpace;
